import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SerieDetailsModal from '../SerieDetailsModal/SerieDetailsModal';
import TemplateModal from '../TemplateModal/TemplateModal';
import './BaseCarousel.css';

const BaseCarousel = ({ title, contents, renderItem }) => {
    const carouselRef = useRef(null);
    const navigate = useNavigate();
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showSerieModal, setShowSerieModal] = useState(false);
    const [selectedContent, setSelectedContent] = useState(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [isMobile] = useState(window.innerWidth <= 768);

    const calculateVisibleItems = () => {
        const container = carouselRef.current;
        if (!container) return 1;

        const containerWidth = container.clientWidth;
        const firstItem = container.firstElementChild;
        if (!firstItem) return 1;

        const itemWidth = firstItem.offsetWidth;
        // Since we removed gap property, we'll use margin-right instead
        const computedStyle = window.getComputedStyle(firstItem);
        const marginRight = parseInt(computedStyle.marginRight) || 0;

        return Math.floor(containerWidth / (itemWidth + marginRight));
    };

    const scroll = (direction) => {
        const { current } = carouselRef;
        if (current) {
            const firstItem = current.firstElementChild;
            if (!firstItem) return;

            const itemWidth = firstItem.offsetWidth;
            // Since we removed gap property, we'll use margin-right instead
            const computedStyle = window.getComputedStyle(firstItem);
            const marginRight = parseInt(computedStyle.marginRight) || 0;
            const itemTotalWidth = itemWidth + marginRight;
            const visibleItems = calculateVisibleItems();
            const scrollAmount = itemTotalWidth * visibleItems;

            if (direction === 'left') {
                const targetScroll = Math.floor((current.scrollLeft - scrollAmount) / itemTotalWidth) * itemTotalWidth;
                current.scrollTo({
                    left: Math.max(0, targetScroll),
                    behavior: 'smooth'
                });
            } else {
                const targetScroll = Math.ceil((current.scrollLeft + scrollAmount) / itemTotalWidth) * itemTotalWidth;
                current.scrollTo({
                    left: Math.min(current.scrollWidth - current.clientWidth, targetScroll),
                    behavior: 'smooth'
                });
            }

            setTimeout(updateScrollButtons, 300);
        }
    };

    const updateScrollButtons = () => {
        const { current } = carouselRef;
        if (current) {
            setCanScrollLeft(current.scrollLeft > 0);
            setCanScrollRight(current.scrollLeft + current.clientWidth < current.scrollWidth - 10);
        }
    };

    const handleClick = (content) => {

        if (content.status !== 'blocked') {
            if (content.type === "serie") {
                if (isMobile) {
                    navigate(`/serie_details/${content.id}`);
                } else {
                    setSelectedContent(content);
                    setShowSerieModal(true);
                }
            } else {
                const availableContents = contents.filter(content => content.status === 'available');
                navigate(`/video/${content.id}`, { state: { playlist: availableContents } });
            }
        } else {
            setSelectedContent(content);
            setShowTemplateModal(true);
        }
    };

    const handleTemplateModalClose = () => {
        setShowTemplateModal(false);
        setSelectedContent(null);
    };

    const handleSerieModalClose = () => {
        setShowSerieModal(false);
        setSelectedContent(null);
    };

    useEffect(() => {
        const current = carouselRef.current;
        if (!current) return;

        updateScrollButtons();

        const handleScroll = () => updateScrollButtons();
        current.addEventListener('scroll', handleScroll);

        // Chrome 79 compatible resize handling
        const handleResize = () => {
            updateScrollButtons();
        };
        
        window.addEventListener('resize', handleResize);

        return () => {
            current.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div className="base-carousel-wrapper">
            <h1 className="carousel-suptitle">{title}</h1>
            <div className="carousel-container">
                <button
                    className="carousel-button left"
                    onClick={() => scroll('left')}
                    disabled={!canScrollLeft}
                >‹</button>

                <div className="carousel" ref={carouselRef}>
                    {contents.map((content) => renderItem(content, handleClick))}
                </div>

                <button
                    className="carousel-button right"
                    onClick={() => scroll('right')}
                    disabled={!canScrollRight}
                >›</button>
            </div>

            {showTemplateModal && selectedContent && (
                <TemplateModal
                    templateId={selectedContent.purchaseTemplateId}
                    onClose={handleTemplateModalClose}
                />
            )}

            {showSerieModal && selectedContent && (
                <SerieDetailsModal
                    serieId={selectedContent.id}
                    onClose={handleSerieModalClose}
                />
            )}
        </div>
    );
};

export default BaseCarousel; 